import { Card } from "react-bootstrap";
import { useAppDispatch } from "utils/redux/hooks";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  getPerformanceAnalysis,
  generatePerformanceReview,
  generateCompetencyFramework,
  generateNinetyDayDevelopmentPlan,
  selectPerformanceAnalysisResponse,
  selectGeneratingPerformanceReviewStatus,
  selectGeneratingCompetencyFrameworkStatus,
  selectGeneratingNinetyDayDevelopmentPlanStatus,
} from "./slice";
import Loading from "app/storybookComponents/Loading";

interface Props {
  readonly jobTitle: string;
  readonly userAccountId: number;
}

// Note: This component is only meant for development and testing purposes.
// (If plan is to use this component in a production environment, please consider cleaning up UI and refactoring)
export default function PerformanceAnalysis({
  jobTitle,
  userAccountId,
}: Props) {
  const dispatch = useAppDispatch();

  // State to track which sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    competencyFramework: false,
    performanceReview: false,
    ninetyDayPlan: false,
  });
  const [competencyFrameworkContext, setCompetencyFrameworkContext] = useState<
    string | null
  >(null);
  const [performanceReviewContext, setPerformanceReviewContext] = useState<
    string | null
  >(null);
  const [ninetyDayDevelopmentPlanContext, setNinetyDayDevelopmentPlanContext] =
    useState<string | null>(null);

  const performanceAnalysisResponse = useSelector(
    selectPerformanceAnalysisResponse
  );
  const generatingPerformanceReviewStatus = useSelector(
    selectGeneratingPerformanceReviewStatus
  );
  const generatingCompetencyFrameworkStatus = useSelector(
    selectGeneratingCompetencyFrameworkStatus
  );
  const generatingNinetyDayDevelopmentPlanStatus = useSelector(
    selectGeneratingNinetyDayDevelopmentPlanStatus
  );

  useEffect(() => {
    if (!performanceAnalysisResponse) {
      dispatch(getPerformanceAnalysis(userAccountId));
    }
  }, [dispatch, performanceAnalysisResponse, userAccountId]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getTitle = (sectionKey: keyof typeof expandedSections) => {
    let title = "";
    switch (sectionKey) {
      case "performanceReview":
        title = "Performance Review";
        break;
      case "competencyFramework":
        title = "Competency Framework";
        break;
      case "ninetyDayPlan":
        title = "90-Day Development Plan";
        break;
    }
    return title;
  };

  const getContext = (sectionKey: keyof typeof expandedSections) => {
    let context = null;
    switch (sectionKey) {
      case "performanceReview":
        context = performanceReviewContext;
        break;
      case "competencyFramework":
        context = competencyFrameworkContext;
        break;
      case "ninetyDayPlan":
        context = ninetyDayDevelopmentPlanContext;
        break;
    }
    return context;
  };

  const getSetContext = (sectionKey: keyof typeof expandedSections) => {
    let setContext = null;
    switch (sectionKey) {
      case "performanceReview":
        setContext = setPerformanceReviewContext;
        break;
      case "competencyFramework":
        setContext = setCompetencyFrameworkContext;
        break;
      case "ninetyDayPlan":
        setContext = setNinetyDayDevelopmentPlanContext;
        break;
    }
    return setContext;
  };

  const getSectionHeader = (section: string) => {
    const sectionKey = section as keyof typeof expandedSections;
    const title = getTitle(sectionKey);
    return (
      <div className="mt-4 mb-2">
        <h3 style={{ display: "inline-block", marginRight: "10px" }}>
          {title}
        </h3>
        <button
          onClick={() => toggleSection(sectionKey)}
          className="btn btn-sm btn-outline-primary"
        >
          {expandedSections[sectionKey] ? "Collapse" : "Expand"}
        </button>
      </div>
    );
  };

  const getSectionContext = (section: string) => {
    const sectionKey = section as keyof typeof expandedSections;
    const context = getContext(sectionKey);
    const setContext = getSetContext(sectionKey);
    return (
      <div className="mb-3">
        <label htmlFor={sectionKey} className="form-label">
          {getTitle(sectionKey)} Context
        </label>
        <textarea
          id={sectionKey}
          className="form-control"
          placeholder={`Enter additional context for the ${getTitle(
            sectionKey
          )}...`}
          value={context || ""}
          onChange={(e) => setContext(e.target.value)}
          rows={3}
        />
      </div>
    );
  };

  const getCompetencyFrameworkSection = () => {
    const loadingCompetencyFramework =
      generatingCompetencyFrameworkStatus === "loading";

    const buttonText = performanceAnalysisResponse?.performanceAnalysis
      ?.competencyFramework
      ? "Regenerate"
      : "Generate";

    if (!expandedSections.competencyFramework) {
      return getSectionHeader("competencyFramework");
    }
    return (
      <>
        {getSectionHeader("competencyFramework")}
        {getSectionContext("competencyFramework")}

        {performanceAnalysisResponse?.performanceAnalysis
          ?.competencyFramework &&
          !loadingCompetencyFramework && (
            <pre
              style={{
                backgroundColor: "#f5f5f5",
                padding: "1rem",
                borderRadius: "4px",
                maxHeight: "500px",
                overflow: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              {
                performanceAnalysisResponse.performanceAnalysis
                  .competencyFramework
              }
            </pre>
          )}

        {loadingCompetencyFramework && (
          <pre
            style={{
              backgroundColor: "#f5f5f5",
              padding: "1rem",
              borderRadius: "4px",
              maxHeight: "500px",
              overflow: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            <Loading />
          </pre>
        )}

        <button
          onClick={() =>
            dispatch(
              generateCompetencyFramework({
                userAccountId,
                jobTitle,
                additionalContext: competencyFrameworkContext,
              })
            )
          }
          className="btn btn-sm btn-outline-primary"
          disabled={loadingCompetencyFramework}
        >
          {buttonText} Competency Framework
        </button>
      </>
    );
  };

  const getPerformanceReviewSection = () => {
    const loadingPerformanceReview =
      generatingPerformanceReviewStatus === "loading";

    const buttonText = performanceAnalysisResponse?.performanceAnalysis
      ?.performanceReview
      ? "Regenerate"
      : "Generate";

    if (!expandedSections.performanceReview) {
      return getSectionHeader("performanceReview");
    }

    // Check if competency framework is required
    const isCompetencyFrameworkMissing =
      !performanceAnalysisResponse?.performanceAnalysis?.competencyFramework;

    return (
      <>
        {getSectionHeader("performanceReview")}
        {getSectionContext("performanceReview")}
        {isCompetencyFrameworkMissing ? (
          <p>
            Competency Framework is required to generate a performance review.
          </p>
        ) : (
          <button
            onClick={() =>
              dispatch(
                generatePerformanceReview({
                  userAccountId,
                  startDate: null,
                  endDate: null,
                  additionalContext: performanceReviewContext,
                })
              )
            }
            className="btn btn-sm btn-outline-primary"
            disabled={loadingPerformanceReview}
          >
            {buttonText} Performance Review
          </button>
        )}

        {performanceAnalysisResponse?.performanceAnalysis?.performanceReview &&
          !loadingPerformanceReview && (
            <div
              className="performance-review"
              style={{
                padding: "1rem",
                backgroundColor: "white",
                border: "1px solid #eaeaea",
                borderRadius: "4px",
                marginTop: "1rem",
              }}
              dangerouslySetInnerHTML={{
                __html:
                  performanceAnalysisResponse.performanceAnalysis
                    .performanceReview,
              }}
            />
          )}

        {loadingPerformanceReview && (
          <pre
            style={{
              backgroundColor: "#f5f5f5",
              padding: "1rem",
              borderRadius: "4px",
              maxHeight: "500px",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              marginTop: "1rem",
            }}
          >
            <Loading />
          </pre>
        )}
      </>
    );
  };

  const getNinetyDayDevelopmentPlanSection = () => {
    const loadingNinetyDayPlan =
      generatingNinetyDayDevelopmentPlanStatus === "loading";

    const buttonText = performanceAnalysisResponse?.performanceAnalysis
      ?.ninetyDayDevelopmentPlan
      ? "Regenerate"
      : "Generate";

    if (!expandedSections.ninetyDayPlan) {
      return getSectionHeader("ninetyDayPlan");
    }

    const isPerformanceReviewMissing =
      !performanceAnalysisResponse?.performanceAnalysis?.performanceReview;

    return (
      <>
        {getSectionHeader("ninetyDayPlan")}
        {getSectionContext("ninetyDayPlan")}
        {isPerformanceReviewMissing ? (
          <p>
            Performance Review is required to generate a 90-day development
            plan.
          </p>
        ) : (
          <button
            onClick={() =>
              dispatch(
                generateNinetyDayDevelopmentPlan({
                  userAccountId,
                  additionalContext: ninetyDayDevelopmentPlanContext,
                })
              )
            }
            className="btn btn-sm btn-outline-primary"
            disabled={loadingNinetyDayPlan}
          >
            {buttonText} 90-Day Development Plan
          </button>
        )}

        {performanceAnalysisResponse?.performanceAnalysis
          ?.ninetyDayDevelopmentPlan &&
          !loadingNinetyDayPlan && (
            <div
              className="ninety-day-plan"
              style={{
                padding: "1rem",
                backgroundColor: "white",
                border: "1px solid #eaeaea",
                borderRadius: "4px",
                marginTop: "1rem",
              }}
              dangerouslySetInnerHTML={{
                __html:
                  performanceAnalysisResponse.performanceAnalysis
                    .ninetyDayDevelopmentPlan,
              }}
            />
          )}

        {loadingNinetyDayPlan && (
          <pre
            style={{
              backgroundColor: "#f5f5f5",
              padding: "1rem",
              borderRadius: "4px",
              maxHeight: "500px",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              marginTop: "1rem",
            }}
          >
            <Loading />
          </pre>
        )}
      </>
    );
  };

  return (
    <div>
      <Card className="p-4">
        <div>
          <div className="mb-2">
            <h2 style={{ display: "inline-block", marginRight: "10px" }}>
              Performance Analysis
            </h2>
          </div>
          {getCompetencyFrameworkSection()}
          {getPerformanceReviewSection()}
          {getNinetyDayDevelopmentPlanSection()}
        </div>
      </Card>
    </div>
  );
}
